/**
 * VIVE Switzerland Custom Scripts
 * Theme: Impulse v1.1.0
 *
 * Contents:
 * 1. Counter Animation (Science Section)
 * 2. Parallax Image Effect (Science Section)
 * 3. Needs Slider — Mobile Flickity Init (vive-home-needs-slider)
 */

/* ===========================
   1. Counter Animation
   =========================== */

function initCounterAnimation() {
  const counters = document.querySelectorAll('[data-counter-animate]');
  if (!counters.length) return;

  const observerOptions = {
    threshold: 0.3,
    rootMargin: '0px'
  };

  // Easing function for smooth count-up
  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  const animateCounter = (target, duration) => {
    const text = target.textContent.trim();
    
    // Store original value for reset
    const counter = target.closest('[data-counter-animate]');
    if (counter && !counter.dataset.originalValue) {
      counter.dataset.originalValue = text;
    }

    // Parse number (e.g., "82 %", "2,5 Mio", "100+")
    const numericMatch = text.match(/[\d.,]+/);
    if (!numericMatch) return;

    const numericPart = numericMatch[0];
    const suffix = text.replace(numericPart, '').trim();
    const targetValue = parseFloat(numericPart.replace(',', '.'));

    if (isNaN(targetValue)) return;

    let start = null;
    const startValue = 0;

    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const easedProgress = easeOutQuart(progress);
      const current = startValue + (targetValue - startValue) * easedProgress;

      // Format number (preserve decimal if original had one)
      let formatted;
      if (numericPart.includes(',') || numericPart.includes('.')) {
        formatted = current.toFixed(1).replace('.', ',');
      } else {
        formatted = Math.round(current).toString();
      }

      target.textContent = formatted + (suffix ? ' ' + suffix : '');

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        // Set final value (exactly as stored)
        target.textContent = text;
      }
    };

    requestAnimationFrame(step);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const counter = entry.target;
      const target = counter.querySelector('[data-counter-target]');
      const duration = parseInt(counter.dataset.counterDuration) || 2000;

      if (entry.isIntersecting) {
        // Entry: Start animation
        if (target) {
          animateCounter(target, duration);
        }
      } else {
        // Exit: Reset to original value
        if (target && counter.dataset.originalValue) {
          target.textContent = counter.dataset.originalValue;
        }
      }
    });
  }, observerOptions);

  counters.forEach(counter => observer.observe(counter));
}

/* ===========================
   2. Parallax Image Effect
   =========================== */

function initParallaxImages() {
  const section = document.querySelector('.vive-home-science');
  if (!section) return;

  const imageBlocks = section.querySelectorAll('.vive-science-block--image');
  if (imageBlocks.length < 2) return;

  // Assign parallax classes
  imageBlocks[0].classList.add('parallax-up');
  if (imageBlocks[1]) imageBlocks[1].classList.add('parallax-down');

  let inViewport = false;
  let ticking = false;

  // Easing function for smooth parallax
  const easeInOutCubic = (t) => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  const updateParallax = () => {
    if (!inViewport) {
      // Exit: Reset parallax to 0 (smooth)
      imageBlocks.forEach(block => {
        block.style.setProperty('--parallax-offset', '0');
      });
      ticking = false;
      return;
    }

    const rect = section.getBoundingClientRect();
    const sectionHeight = rect.height;
    const viewportHeight = window.innerHeight;

    // Calculate scroll progress (0 = entering viewport, 1 = leaving viewport)
    const scrollStart = rect.top - viewportHeight;
    const scrollEnd = rect.bottom;
    const scrollRange = scrollEnd - scrollStart;
    const scrolled = -scrollStart;
    const progress = Math.max(0, Math.min(1, scrolled / scrollRange));

    // Apply easing for smoother movement
    const easedProgress = easeInOutCubic(progress);

    // Max parallax offset in pixels (increased for stronger effect)
    const maxOffset = 80;
    const offset = easedProgress * maxOffset;

    imageBlocks.forEach(block => {
      block.style.setProperty('--parallax-offset', offset.toFixed(2));
    });

    ticking = false;
  };

  // Intersection Observer for entry/exit detection
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      inViewport = entry.isIntersecting;
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    });
  }, {
    threshold: 0,
    rootMargin: '0px'
  });

  observer.observe(section);

  // Scroll listener (only when in viewport)
  let scrollTicking = false;
  window.addEventListener('scroll', () => {
    if (!inViewport || scrollTicking) return;
    window.requestAnimationFrame(() => {
      updateParallax();
      scrollTicking = false;
    });
    scrollTicking = true;
  }, { passive: true });
}

/* ===========================
   3. FAQ Accordion
   =========================== */

function initFaqAccordion() {
  const faqs = document.querySelectorAll('.vive-faq__list');
  if (!faqs.length) return;

  faqs.forEach(function(list) {
    list.addEventListener('click', function(e) {
      const btn = e.target.closest('.vive-faq__question');
      if (!btn) return;

      const item = btn.closest('.vive-faq__item');
      const isActive = item.classList.contains('active');

      // Close all items in this list
      list.querySelectorAll('.vive-faq__item.active').forEach(function(el) {
        el.classList.remove('active');
        el.querySelector('.vive-faq__question').setAttribute('aria-expanded', 'false');
      });

      if (!isActive) {
        item.classList.add('active');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

/* ===========================
   4. Needs Slider — Mobile Flickity
   =========================== */

function initNeedsSlider() {
  const sliders = document.querySelectorAll('[data-slider]');
  if (!sliders.length) return;

  // Flickity is already loaded by Impulse theme
  if (typeof Flickity === 'undefined') return;

  sliders.forEach(function(el) {
    // Only activate slider on mobile
    const mq = window.matchMedia('(max-width: 768px)');

    function mountSlider() {
      if (el.flickityInstance) return;
      const autoplay = el.dataset.autoplay === 'true';
      const speed = parseInt(el.dataset.autplaySpeed) || 5000;

      el.flickityInstance = new Flickity(el, {
        cellAlign: 'left',
        contain: true,
        draggable: '>1',
        prevNextButtons: false,
        pageDots: true,
        autoPlay: autoplay ? speed : false,
        adaptiveHeight: true,
        wrapAround: false,
        groupCells: false,
        setGallerySize: true,
        percentPosition: true
      });

      // Nach Init + kurz verzögert resize für korrekte Höhe
      el.flickityInstance.on('ready', function() {
        setTimeout(function() { el.flickityInstance.resize(); }, 50);
      });
    }

    function destroySlider() {
      if (el.flickityInstance) {
        el.flickityInstance.destroy();
        el.flickityInstance = null;
      }
    }

    function handleBreakpoint(e) {
      if (e.matches) {
        mountSlider();
      } else {
        destroySlider();
      }
    }

    mq.addEventListener('change', handleBreakpoint);
    handleBreakpoint(mq);
  });
}

/* ===========================
   4. VIVE Reviews Slider — Flickity Init
   3-up: aktive Slide in Mitte hervorgehoben
   keine transform:scale — stabile Flickity-Höhenberechnung
   =========================== */

function initReviewsSlider() {
  var tracks = document.querySelectorAll('[data-vive-reviews-slider]');
  if (!tracks.length) return;

  function _init() {
    tracks.forEach(function(el) {
      // Bereits initialisiert? Überspringen.
      if (el._viveFlickity) return;

      var autoplay = el.dataset.autoplay === 'true';
      var speed    = parseInt(el.dataset.autoplaySpeed, 10) || 4000;

      /* Infinite Center-Slider: aktive Card ist mittig und grösser,
         Nachbar-Cards seitlich kleiner und vertikal zentriert (via CSS translateY).
         wrapAround:true auf allen Breakpoints — kein Deadend, keine Missing-Card.
         contain:false ist Voraussetzung für wrapAround. */
      var flkty = new Flickity(el, {
        cellAlign:            'center',
        contain:              false,
        wrapAround:           true,
        pageDots:             true,
        prevNextButtons:      true,
        adaptiveHeight:       false,
        setGallerySize:       true,
        selectedAttraction:   0.07,
        friction:             0.72,
        autoPlay:             autoplay ? speed : false,
        pauseAutoPlayOnHover: true,
        imagesLoaded:         false,
        percentPosition:      true
      });

      // Nach Init: resize damit Zellpositionen stimmen, dann nochmal nach Fonts
      flkty.on('ready', function() {
        setTimeout(function() {
          flkty.resize();
        }, 80);
        // Zweiter resize nach 400ms für Fonts/Images
        setTimeout(function() {
          flkty.resize();
        }, 400);
      });

      el._viveFlickity = flkty;
    });
  }

  if (typeof Flickity !== 'undefined') {
    _init();
  } else {
    // Warten bis Impulse's Flickity geladen ist
    var attempts = 0;
    var wait = setInterval(function() {
      attempts++;
      if (typeof Flickity !== 'undefined') {
        clearInterval(wait);
        _init();
      }
      if (attempts > 60) clearInterval(wait);
    }, 100);
  }
}

function initReviewsSliderAutoplay() {} // Legacy-Stub

/* ===========================
   4. VIVE Hero Slideshow
   =========================== */

function initViveHeroSlider() {
  var track = document.querySelector('[data-vive-hero]');
  if (!track) return;

  var slideCount = parseInt(track.getAttribute('data-slide-count') || '1', 10);
  if (slideCount <= 1) return; // Kein Slider nötig bei nur einem Slide

  var sectionId = track.closest('[data-section-id]').getAttribute('data-section-id');
  var autoplay  = track.getAttribute('data-autoplay') === 'true';
  var speed     = parseInt(track.getAttribute('data-autoplay-speed') || '6000', 10);
  var dotsEl    = document.querySelector('.vive-hero-dots--' + sectionId);

  function _init() {
    var flkty = new Flickity(track, {
      cellAlign:          'left',
      contain:            true,
      wrapAround:         true,
      autoPlay:           autoplay ? speed : false,
      pauseAutoPlayOnHover: true,
      prevNextButtons:    false,  // eigene Buttons
      pageDots:           false,  // eigene Dots
      selectedAttraction: 0.08,
      friction:           0.75,
      adaptiveHeight:     false,
      lazyLoad:           1,
      imagesLoaded:       true
    });

    // Dots synchronisieren
    function syncDots(index) {
      if (!dotsEl) return;
      var dots = dotsEl.querySelectorAll('.vive-hero-dot');
      dots.forEach(function(d, i) {
        d.classList.toggle('is-active', i === index);
        d.setAttribute('aria-selected', i === index);
        // Animation-Klasse neu starten (Bars)
        if (i === index) {
          d.style.animation = 'none';
          // eslint-disable-next-line no-unused-expressions
          d.offsetHeight; // reflow
          d.style.animation = '';
        }
      });
    }

    flkty.on('change', function(index) {
      syncDots(index);
      // Video-Slides: Video abspielen/pausieren
      track.querySelectorAll('.vive-hero__video').forEach(function(v, i) {
        var slide = v.closest('.vive-hero__slide');
        var slideIndex = parseInt(slide.getAttribute('data-index') || '0', 10);
        if (slideIndex === index) {
          v.play().catch(function() {});
        } else {
          v.pause();
          v.currentTime = 0;
        }
      });
    });

    // Dot-Klick → Slide
    if (dotsEl) {
      dotsEl.querySelectorAll('.vive-hero-dot').forEach(function(dot) {
        dot.addEventListener('click', function() {
          var idx = parseInt(this.getAttribute('data-index') || '0', 10);
          flkty.select(idx);
        });
      });
    }

    // Eigene Pfeil-Buttons
    var prevBtn = document.querySelector('[data-vive-hero-prev="' + sectionId + '"]');
    var nextBtn = document.querySelector('[data-vive-hero-next="' + sectionId + '"]');
    if (prevBtn) prevBtn.addEventListener('click', function() { flkty.previous(); });
    if (nextBtn) nextBtn.addEventListener('click', function() { flkty.next(); });

    // Pause-Button
    var pauseBtn = document.querySelector('[data-vive-hero-pause="' + sectionId + '"]');
    if (pauseBtn) {
      var paused = false;
      pauseBtn.addEventListener('click', function() {
        paused = !paused;
        if (paused) {
          flkty.stopPlayer();
          pauseBtn.querySelector('.vive-hero-pause__stop').style.display = 'none';
          pauseBtn.querySelector('.vive-hero-pause__play').style.display = '';
        } else {
          flkty.playPlayer();
          pauseBtn.querySelector('.vive-hero-pause__stop').style.display = '';
          pauseBtn.querySelector('.vive-hero-pause__play').style.display = 'none';
        }
      });
    }

    // Initialen Dot setzen
    syncDots(0);

    // Resize nach Font-Load
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function() { flkty.resize(); });
    }
  }

  if (typeof Flickity !== 'undefined') {
    _init();
  } else {
    var attempts = 0;
    var wait = setInterval(function() {
      attempts++;
      if (typeof Flickity !== 'undefined') {
        clearInterval(wait);
        _init();
      }
      if (attempts > 80) clearInterval(wait);
    }, 100);
  }
}

/* ===========================
  5. Floating toolbar docking near footer
   =========================== */

function initViveHeaderLangSwitcher() {
  var trigger = document.getElementById('vive-lang-trigger');
  var menu = document.getElementById('vive-lang-menu');
  if (!trigger || !menu) return;

  var storageKey = 'vive_locale';
  var available = [];
  var current = document.documentElement.lang || 'de';

  try {
    var availableAttr = menu.getAttribute('data-available-locales');
    if (availableAttr) available = JSON.parse(availableAttr);
  } catch (e) {
    available = [];
  }

  var currentAttr = menu.getAttribute('data-current-locale');
  if (currentAttr) current = currentAttr;

  function close() {
    menu.setAttribute('hidden', '');
    trigger.setAttribute('aria-expanded', 'false');
  }

  function open() {
    menu.removeAttribute('hidden');
    trigger.setAttribute('aria-expanded', 'true');
    var first = menu.querySelector('.vive-lang-option');
    if (first) first.focus();
  }

  trigger.addEventListener('click', function() {
    if (menu.hasAttribute('hidden')) open();
    else close();
  });

  document.addEventListener('click', function(e) {
    if (!trigger.contains(e.target) && !menu.contains(e.target)) close();
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape') close();
  });

  menu.querySelectorAll('.vive-lang-option').forEach(function(btn) {
    btn.addEventListener('click', function() {
      try {
        localStorage.setItem(storageKey, btn.dataset.locale);
      } catch (err) {
        /* ignore */
      }
    });
  });

  if (!localStorage.getItem(storageKey) && available.length) {
    var nav = (navigator.languages && navigator.languages[0]) || navigator.language || '';
    var navLower = nav.toLowerCase();
    var code =
      navLower.indexOf('zh') === 0 && available.indexOf('zh-CN') > -1
        ? 'zh-CN'
        : navLower.split('-')[0];
    if (code && available.indexOf(code) > -1 && code !== current) {
      var autoBtn = menu.querySelector('[data-locale="' + code + '"]');
      if (autoBtn) {
        try {
          localStorage.setItem(storageKey, code);
        } catch (err2) {
          /* ignore */
        }
        autoBtn.click();
      }
    }
  }
}

function initToolbarSelectorDocking() {
  var overlaySelectors = document.querySelector('.vive-floating-toolbar__selectors');
  var overlaySocial = document.querySelector('.vive-floating-toolbar__social');
  if (overlaySelectors) {
    document.body.classList.add('vive-has-floating-selectors');
  }

  if (!overlaySelectors && !overlaySocial) return;

  var footer = document.querySelector('.site-footer');
  var footerAnchor =
    document.querySelector('.site-footer .footer__section .multi-selectors') ||
    document.querySelector('.site-footer .footer__section') ||
    footer;

  if (!footer) return;

  var setDocked = function(isDocked) {
    document.body.classList.toggle('vive-toolbar-docked', isDocked);

    if (isDocked) {
      var selectors = document.querySelector('[data-vive-floating-selectors]');
      if (selectors) {
        selectors.querySelectorAll('.disclosure-list--visible').forEach(function(panel) {
          panel.classList.remove('disclosure-list--visible');
        });
        selectors.querySelectorAll('[data-disclosure-toggle][aria-expanded="true"]').forEach(function(toggle) {
          toggle.setAttribute('aria-expanded', 'false');
        });
      }

      document.body.classList.remove('vive-selector-open');
    }
  };

  var checkFooterDocking = function() {
    var footerRect = footer.getBoundingClientRect();
    var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    setDocked(footerRect.top < viewportHeight * 0.88);
  };

  if (typeof IntersectionObserver !== 'undefined' && footerAnchor) {
    var observer = new IntersectionObserver(
      function(entries) {
        entries.forEach(function(entry) {
          setDocked(entry.isIntersecting);
        });
      },
      {
        root: null,
        threshold: 0.01,
        rootMargin: '0px 0px -12% 0px'
      }
    );

    observer.observe(footerAnchor);
  }

  window.addEventListener('scroll', function() {
    window.requestAnimationFrame(checkFooterDocking);
  }, { passive: true });
  window.addEventListener('resize', checkFooterDocking);
  checkFooterDocking();
}

function initFloatingSocialToggle() {
  var toolbars = document.querySelectorAll('[data-vive-social-collapsible]');
  if (!toolbars.length) return;

  var hero = document.querySelector('.vive-home-hero, .vive-lp-hero, .index-section--hero, .hero');

  toolbars.forEach(function(toolbar) {
    var toggle = toolbar.querySelector('[data-vive-social-toggle]');
    var items = toolbar.querySelectorAll('.vive-floating-toolbar__social-list li');
    if (!toggle || !items.length) return;

    var centerOffset = (items.length - 1) / 2;
    var isEven = items.length % 2 === 0;

    items.forEach(function(item, index) {
      var expandedShift;

      if (isEven) {
        expandedShift = index < items.length / 2 ? -0.5 : 0.5;
      } else {
        expandedShift = index >= centerOffset ? 1 : 0;
      }

      item.style.setProperty('--vive-social-item-index', index);
      item.style.setProperty('--vive-social-collapse-shift', centerOffset - index);
      item.style.setProperty('--vive-social-expanded-shift', expandedShift);
    });

    var setExpanded = function(isExpanded) {
      toolbar.classList.toggle('is-expanded', isExpanded);
      toolbar.classList.toggle('is-collapsed', !isExpanded);
      toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

      var label = isExpanded ? toggle.dataset.closeLabel : toggle.dataset.openLabel;
      if (label) {
        toggle.setAttribute('aria-label', label);
        var fallback = toggle.querySelector('.icon__fallback-text');
        if (fallback) fallback.textContent = label;
      }
    };

    var userTouched = false;

    toggle.addEventListener('click', function() {
      userTouched = true;
      setExpanded(!toolbar.classList.contains('is-expanded'));
    });

    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' && toolbar.classList.contains('is-expanded')) {
        userTouched = true;
        setExpanded(false);
        toggle.focus();
      }
    });

    setExpanded(false);

    if (hero && typeof IntersectionObserver !== 'undefined') {
      var observer = new IntersectionObserver(
        function(entries) {
          if (userTouched) return;

          entries.forEach(function(entry) {
            setExpanded(!entry.isIntersecting);
          });
        },
        {
          root: null,
          threshold: 0.02,
          rootMargin: '0px 0px -58% 0px'
        }
      );

      observer.observe(hero);
    }
  });
}

var viveFloatingChromeReady = false;

function initViveFloatingChrome() {
  if (viveFloatingChromeReady) return;
  viveFloatingChromeReady = true;

  initToolbarSelectorDocking();
  initFloatingSocialToggle();
  initFloatingSelectorPanels();
}

function initFloatingSelectorPanels() {
  var selectors = document.querySelector('[data-vive-floating-selectors]');
  if (!selectors || selectors.dataset.vivePanelsReady === 'true') return;

  var panels = Array.prototype.slice.call(
    selectors.querySelectorAll('[data-vive-selector-panel][data-disclosure-list]')
  );
  if (!panels.length) return;

  var syncing = false;
  var syncScheduled = false;
  var lastOpenPanelId = '';

  panels.forEach(function(panel) {
    panel.addEventListener('click', function(event) {
      if (event.target.closest('[data-disclosure-option]')) {
        return;
      }
      event.stopPropagation();
    });
  });

  selectors.dataset.vivePanelsReady = 'true';

  var getToggleForPanel = function(panel) {
    var panelId = panel.getAttribute('id');
    if (!panelId) return null;
    return selectors.querySelector('[data-disclosure-toggle][aria-controls="' + panelId + '"]');
  };

  var closePanel = function(panel, toggle) {
    if (!panel || !panel.classList.contains('disclosure-list--visible')) return;
    panel.classList.remove('disclosure-list--visible');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  };

  var positionPanel = function(toggle, panel) {
    if (!panel) return;
    panel.style.removeProperty('--vive-selector-panel-width');
    panel.style.removeProperty('--vive-selector-panel-left');
    panel.style.removeProperty('--vive-selector-panel-bottom');
    panel.style.removeProperty('--vive-selector-panel-shift');
  };

  var syncOpenState = function() {
    if (syncing) return;
    syncing = true;
    syncScheduled = false;

    var openPanel = null;
    var openToggle = null;

    panels.forEach(function(panel) {
      if (!panel.classList.contains('disclosure-list--visible')) return;
      openPanel = panel;
      openToggle = getToggleForPanel(panel);
    });

    var isOpen = Boolean(openPanel);
    var openPanelId = openPanel ? openPanel.id : '';
    document.body.classList.toggle('vive-selector-open', isOpen);

    if (!isOpen) {
      lastOpenPanelId = '';
      syncing = false;
      return;
    }

    if (openPanelId !== lastOpenPanelId) {
      panels.forEach(function(panel) {
        if (panel !== openPanel) {
          closePanel(panel, getToggleForPanel(panel));
        }
      });
      lastOpenPanelId = openPanelId;
    }

    if (openToggle) {
      openToggle.setAttribute('aria-expanded', 'true');
      positionPanel(openToggle, openPanel);
    }

    syncing = false;
  };

  var scheduleSync = function() {
    if (syncScheduled) return;
    syncScheduled = true;
    window.requestAnimationFrame(syncOpenState);
  };

  var scheduleReposition = function() {
    if (!document.body.classList.contains('vive-selector-open')) return;
    scheduleSync();
  };

  document.addEventListener('keydown', function(event) {
    if (event.key !== 'Escape') return;
    document.body.classList.remove('vive-selector-open');
  });

  window.addEventListener('resize', scheduleReposition, { passive: true });
  window.addEventListener('scroll', scheduleReposition, { passive: true });

  panels.forEach(function(panel) {
    var observer = new MutationObserver(function() {
      if (syncing) return;
      scheduleSync();
    });
    observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
  });
}

/* ===========================
   Recently viewed — locale-safe URLs
   Impulse stores full URLs in localStorage (mixed /zh/, /en/, …).
   =========================== */

const VIVE_LOCALE_PREFIX_RE = /^\/(de|en|fr|it|zh|zh-cn)(?=\/|$)/i;
const VIVE_RECENT_LOCALE_KEY = 'vive-recent-locale';
const VIVE_RECENT_STORAGE_KEY = 'theme-recent';

function viveGetLocaleCode() {
  return (document.documentElement.lang || 'de').toLowerCase();
}

function viveGetLocaleRoot() {
  const home = window.theme && theme.routes && theme.routes.home ? theme.routes.home : '/';
  if (!home || home === '/') return '';
  return home.replace(/\/$/, '');
}

function viveExtractProductPath(url) {
  if (!url) return '';
  const raw = String(url);
  const pathOnly = raw.indexOf('://') !== -1 ? raw.replace(/^https?:\/\/[^/]+/i, '') : raw;
  const match = pathOnly.match(/\/products\/[^/?#]+/i);
  return match ? match[0] : pathOnly.split('?')[0];
}

function viveStripLocalePrefix(path) {
  let pathValue = path || '';
  if (!pathValue.startsWith('/')) pathValue = '/' + pathValue;
  pathValue = pathValue.replace(VIVE_LOCALE_PREFIX_RE, '');
  if (!pathValue.startsWith('/')) pathValue = '/' + pathValue;
  return pathValue;
}

function viveLocalizeProductUrl(url) {
  const productPath = viveStripLocalePrefix(viveExtractProductPath(url));
  const root = viveGetLocaleRoot();
  return (root || '') + productPath;
}

function viveMigrateRecentStorage() {
  if (!window.theme || !theme.recentlyViewed) return;

  let recent = theme.recentlyViewed.recent;
  if (theme.config && theme.config.hasLocalStorage) {
    try {
      const stored = window.localStorage.getItem(VIVE_RECENT_STORAGE_KEY);
      if (stored) recent = JSON.parse(stored);
    } catch (error) {
      return;
    }
  }

  if (!recent || typeof recent !== 'object') return;

  let changed = false;
  Object.keys(recent).forEach(function(handle) {
    const entry = recent[handle];
    if (!entry || typeof entry !== 'object') return;

    if (entry.url) {
      if (!entry.paths) entry.paths = {};
      const locale = viveGetLocaleCode();
      entry.paths[locale] = viveExtractProductPath(entry.url);
      delete entry.url;
      changed = true;
    }
  });

  theme.recentlyViewed.recent = recent;
  if (changed && theme.config && theme.config.hasLocalStorage) {
    window.localStorage.setItem(VIVE_RECENT_STORAGE_KEY, JSON.stringify(recent));
  }
}

function viveClearRecentProductCacheIfLocaleChanged() {
  if (!theme.config || !theme.config.hasSessionStorage) return;
  const current = viveGetLocaleCode();
  const stored = sessionStorage.getItem(VIVE_RECENT_LOCALE_KEY);
  if (stored && stored !== current) {
    sessionStorage.removeItem('recent-products');
    if (window.theme && theme.recentlyViewed) {
      theme.recentlyViewed.productInfo = {};
    }
  }
  sessionStorage.setItem(VIVE_RECENT_LOCALE_KEY, current);
}

function viveRecentUrlForHandle(handle, fallbackUrl) {
  const entry = window.theme && theme.recentlyViewed && theme.recentlyViewed.recent
    ? theme.recentlyViewed.recent[handle]
    : null;
  const locale = viveGetLocaleCode();
  if (entry && entry.paths && entry.paths[locale]) {
    return viveLocalizeProductUrl(entry.paths[locale]);
  }
  return viveLocalizeProductUrl(fallbackUrl || '/products/' + handle);
}

let viveRecentPatched = false;

function vivePatchRecentlyViewed() {
  if (!window.theme || viveRecentPatched) return;
  viveRecentPatched = true;

  viveMigrateRecentStorage();
  viveClearRecentProductCacheIfLocaleChanged();

  if (theme.Product && theme.Product.prototype && theme.Product.prototype.addIdToRecentlyViewed) {
    const originalAdd = theme.Product.prototype.addIdToRecentlyViewed;
    theme.Product.prototype.addIdToRecentlyViewed = function() {
      originalAdd.call(this);
      const handle = this.container.getAttribute('data-product-handle');
      const productUrl = this.container.getAttribute('data-product-url');
      if (!handle || !theme.recentlyViewed.recent[handle]) return;

      const entry = theme.recentlyViewed.recent[handle];
      if (!entry.paths) entry.paths = {};
      entry.paths[viveGetLocaleCode()] = viveExtractProductPath(productUrl);
      delete entry.url;

      if (theme.config && theme.config.hasLocalStorage) {
        window.localStorage.setItem(VIVE_RECENT_STORAGE_KEY, JSON.stringify(theme.recentlyViewed.recent));
      }
    };
  }

  if (typeof theme.buildProductGridItem === 'function') {
    const originalBuild = theme.buildProductGridItem;
    theme.buildProductGridItem = function(items, gridWidth, rowOf, imageSizes) {
      if (Array.isArray(items)) {
        items.forEach(function(product) {
          if (!product || !product.handle) return;
          product.url = viveRecentUrlForHandle(product.handle, product.url);
        });
      }
      return originalBuild(items, gridWidth, rowOf, imageSizes);
    };
  }

  if (theme.RecentlyViewed && theme.RecentlyViewed.prototype && theme.RecentlyViewed.prototype.getProductInfo) {
    const originalGet = theme.RecentlyViewed.prototype.getProductInfo;
    theme.RecentlyViewed.prototype.getProductInfo = function(handle) {
      return new Promise(function(resolve, reject) {
        if (theme.recentlyViewed.productInfo.hasOwnProperty(handle)) {
          const cached = theme.recentlyViewed.productInfo[handle];
          let parsed = cached;
          if (typeof cached === 'string') {
            try {
              parsed = JSON.parse(cached);
            } catch (error) {
              resolve(cached);
              return;
            }
          }
          if (parsed && parsed.handle) {
            parsed.url = viveRecentUrlForHandle(parsed.handle, parsed.url);
            resolve(JSON.stringify(parsed));
            return;
          }
          resolve(cached);
          return;
        }

        const root = viveGetLocaleRoot();
        const fetchUrl = (root || '') + '/products/' + encodeURIComponent(handle) + '.js';
        fetch(fetchUrl)
          .then(function(response) {
            return response.text();
          })
          .then(function(product) {
            resolve(product);
          })
          .catch(reject);
      });
    };
  }
}

function initViveRecentlyViewed() {
  vivePatchRecentlyViewed();
}

/* ===========================
   Initialize on DOM Ready
   =========================== */

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initViveRecentlyViewed();
    initCounterAnimation();
    initParallaxImages();
    initFaqAccordion();
    initNeedsSlider();
    initReviewsSlider();
    initViveHeroSlider();
    initViveHeaderLangSwitcher();
    initViveFloatingChrome();
  });
} else {
  initViveRecentlyViewed();
  initCounterAnimation();
  initParallaxImages();
  initFaqAccordion();
  initNeedsSlider();
  initReviewsSlider();
  initViveHeroSlider();
  initViveHeaderLangSwitcher();
  initViveFloatingChrome();
}

// Footer copyright: shop.name "VIVE Switzerland" → "VIVE SWITZERLAND"
document.querySelectorAll('.footer__small-text').forEach(function(el) {
  el.childNodes.forEach(function(node) {
    if (node.nodeType === 3) {
      node.textContent = node.textContent.replace(/VIVE Switzerland/g, 'VIVE SWITZERLAND');
    }
  });
});
